import React from 'react'
import { Route } from 'react-router-dom'
import { AppMode } from '@customTypes/Generic'
import { RescheduleProvider } from './react/context/RescheduleContext'
import routes from './config/routes'
import { Treatment } from './react/pages/Treatment/Treatment'
import Patient from './react/pages/Patient/Patient'
import Practitioner from './react/pages/Practitioner'
import Contact from './react/pages/Contact'
import Success from './react/pages/Success'
import AppointmentConfirmationRecall from './react/pages/AppointmentConfirmationRecall'
import AppointmentConfirmationGapFiller from './react/pages/AppointmentConfirmationGapFiller'
import { OnlineCancellation } from './react/pages/OnlineCancellation'
import { useSetInitialChoice } from './react/hooks/useSetInitialChoice'
import { ErrorBoundary } from './react/components/ErrorBoundary/ErrorBoundary'
import { AfterBookingFallback } from './react/components/ErrorBoundary/AfterBookingFallback'
import { BookByPhone } from './react/pages/BookByPhone'
import { UpsellTreatment } from './react/pages/UpsellTreatment'
import { PhoneVerification } from './react/pages/PhoneVerification'
import { Survey } from './react/pages/Survey'
import { OldPatientPhoneVerification } from './react/pages/OldPatientPhoneVerification'
import { SelectPatientAccount } from './react/pages/SelectPatientAccount'
import AppointmentPage from './react/pages/Appointment'
import { Clinic } from './react/pages/Clinic'
import { PainDetails } from './react/pages/PainDetails'
import { Loading } from './react/pages/Loading'
import { District } from './react/pages/District'

const RegularRouting = () => {
  return (
    <>
      <Route exact path={routes.home} component={Treatment} />
      <Route exact path={routes.treatment} component={Treatment} />
      <Route exact path={routes.district} component={District} />
      <Route exact path={routes.clinic} component={Clinic} />
      <Route exact path={routes.upsell} component={UpsellTreatment} />
      <Route exact path={routes.painDetails} component={PainDetails} />
      <Route exact path={routes.patient} component={Patient} />
      <Route exact path={routes.oldPatientPhoneVerification} component={OldPatientPhoneVerification} />
      <Route exact path={routes.oldPatientSelectAccount} component={SelectPatientAccount} />
      <Route exact path={routes.practitioner} component={Practitioner} />
      <Route exact path={routes.appointment} component={AppointmentPage} />
      <ErrorBoundary fallback={<AfterBookingFallback />} additionalDebugData="Contact Page">
        <Route exact path={routes.contact} component={Contact} />
      </ErrorBoundary>
      <Route exact path={routes.phoneVerification} component={PhoneVerification} />
      <ErrorBoundary fallback={<AfterBookingFallback />} additionalDebugData="Success Page">
        <Route exact path={routes.success} component={Success} />
      </ErrorBoundary>
      <Route exact path={routes.bookByPhone} component={BookByPhone} />
    </>
  )
}

/**
 * Based on the recal email that is sent, the patient
 * is given a list of possible date/time options and
 * the option to select a custom date.
 * All links in the email are pointing to /appointment
 */
const PatientRecallRouting = () => {
  return (
    <>
      <Route exact path="/" component={AppointmentConfirmationRecall} />
      <Route path="/appointment" component={AppointmentPage} />
      <Route exact path={routes.success} component={Success} />
    </>
  )
}

const PatientGapFillerRouting = () => {
  return (
    <>
      <Route exact path="/" component={AppointmentConfirmationGapFiller} />
      <Route path="/appointment" component={AppointmentPage} />
      <Route exact path={routes.success} component={Success} />
    </>
  )
}

const CancelRouting = () => {
  return (
    <>
      <Route exact path="/" component={OnlineCancellation} />
    </>
  )
}

const SurveyRouting = () => {
  return (
    <>
      <Route exact path="*" component={Survey} />
    </>
  )
}

const routing = {
  [AppMode.NORMAL]: RegularRouting,
  [AppMode.RECALL]: PatientRecallRouting,
  [AppMode.CANCEL]: CancelRouting,
  [AppMode.GAP_FILLER]: PatientGapFillerRouting,
  [AppMode.SURVEY]: SurveyRouting,
}

type Props = {
  mode?: AppMode
}

const AppRouter = ({ mode = AppMode.NORMAL }: Props) => {
  const initialized = useSetInitialChoice(mode)
  if (!initialized) return <Loading />

  const Routes = routing[mode]
  return (
    <RescheduleProvider>
      <Routes />
    </RescheduleProvider>
  )
}

export default AppRouter
